import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase-config';
import Toast from '../components/UI/Toast';

const Contact = () => {
  const [settings, setSettings] = useState({});
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
  const [toast, setToast] = useState(null);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const settingsRef = doc(db, 'settings', 'college_info');
        const settingsSnap = await getDoc(settingsRef);
        if (settingsSnap.exists()) {
          setSettings(settingsSnap.data());
        }
      } catch (error) {
        console.error('Error loading settings:', error);
      }
    };
    loadSettings();
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    setToast({ message: 'Message sent successfully! We will get back to you soon.', type: 'success' });
    setFormData({ name: '', email: '', phone: '', message: '' });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="container contact-page">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      
      <motion.h1
        className="contact-page-title"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <i className="fas fa-envelope" style={{ color: 'var(--clr-blue)' }}></i> Contact Us
      </motion.h1>

      <div className="contact-grid">
        {/* Contact Info */}
        <motion.div
          className="contact-info-card"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <h3>Get in Touch</h3>
          
          <div className="contact-info-item">
            <div className="contact-icon" style={{ background: 'var(--clr-blue)' }}>
              <i className="fas fa-map-marker-alt"></i>
            </div>
            <div>
              <h4>Address</h4>
              <p>{settings.address || '123 Education City, Mumbai - 400001'}</p>
            </div>
          </div>

          <div className="contact-info-item">
            <div className="contact-icon" style={{ background: 'var(--clr-emerald)' }}>
              <i className="fas fa-phone"></i>
            </div>
            <div>
              <h4>Phone</h4>
              <p>{settings.phone || '+91 1234567890'}</p>
            </div>
          </div>

          <div className="contact-info-item">
            <div className="contact-icon" style={{ background: 'var(--clr-amber)' }}>
              <i className="fas fa-envelope"></i>
            </div>
            <div>
              <h4>Email</h4>
              <p>{settings.email || 'info@dolphin.edu'}</p>
            </div>
          </div>

          <div className="office-hours">
            <h4><i className="fas fa-clock"></i> Office Hours</h4>
            <div className="office-hours-item">
              <span>Monday - Friday</span>
              <span>9:00 AM - 6:00 PM</span>
            </div>
            <div className="office-hours-item">
              <span>Saturday</span>
              <span>10:00 AM - 4:00 PM</span>
            </div>
            <div className="office-hours-item">
              <span>Sunday</span>
              <span>Closed</span>
            </div>
          </div>
        </motion.div>

        {/* Contact Form */}
        <motion.div
          className="contact-form-card"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <h3><i className="fas fa-paper-plane"></i> Send us a Message</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <input
                type="text"
                name="name"
                placeholder="Your Full Name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-group">
              <input
                type="email"
                name="email"
                placeholder="Your Email Address"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-group">
              <input
                type="tel"
                name="phone"
                placeholder="Phone Number"
                value={formData.phone}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <textarea
                name="message"
                placeholder="Your Message"
                rows="4"
                value={formData.message}
                onChange={handleChange}
                required
              />
            </div>
            <button type="submit" className="submit-btn">
              <i className="fas fa-paper-plane"></i> Send Message
            </button>
          </form>
        </motion.div>
      </div>

      {/* Map */}
      <motion.div
        className="contact-map"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.6 }}
      >
        <iframe
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3770.0000000000005!2d72.8777!3d19.0760!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be7c63000000000%3A0x0!2zMTnCsDA0JzMzLjYiTiA3MsKwNTInMzkuNiJF!5e0!3m2!1sen!2sin!4v1700000000000!5m2!1sen!2sin"
          title="College Location"
          allowFullScreen=""
          loading="lazy"
        />
      </motion.div>

      {/* Add CSS for responsiveness */}
      <style>{`
        /* Contact Page Styles */
        .contact-page {
          padding-top: 40px;
        }

        .contact-page-title {
          font-family: 'Playfair Display', serif;
          font-size: 2.5rem;
          margin-bottom: 30px;
        }

        /* Contact Grid */
        .contact-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 30px;
          margin-bottom: 60px;
        }

        /* Contact Info Card */
        .contact-info-card {
          background: rgba(255, 255, 255, 0.5);
          backdrop-filter: blur(20px) saturate(140%);
          border-radius: 24px;
          padding: 32px;
          border: 1px solid var(--clr-blue-border);
        }

        .contact-info-card h3 {
          font-family: 'Playfair Display', serif;
          margin-bottom: 25px;
        }

        .contact-info-item {
          display: flex;
          align-items: center;
          gap: 18px;
          padding: 12px;
          margin-bottom: 20px;
          border-radius: 14px;
          transition: all 0.3s ease;
        }

        .contact-info-item:hover {
          background: rgba(255, 255, 255, 0.3);
          transform: translateX(5px);
        }

        .contact-icon {
          width: 55px;
          height: 55px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 1.3rem;
          flex-shrink: 0;
        }

        .contact-info-item h4 {
          font-family: 'Playfair Display', serif;
          font-size: 1rem;
          margin-bottom: 5px;
        }

        .contact-info-item p {
          color: var(--text-secondary);
          font-size: 0.85rem;
        }

        /* Office Hours */
        .office-hours {
          margin-top: 20px;
          padding-top: 20px;
          border-top: 1px solid var(--clr-blue-border);
        }

        .office-hours h4 {
          font-family: 'Playfair Display', serif;
          margin-bottom: 15px;
        }

        .office-hours-item {
          display: flex;
          justify-content: space-between;
          padding: 10px 0;
          border-bottom: 1px solid rgba(0, 0, 0, 0.05);
        }

        .office-hours-item:last-child {
          border-bottom: none;
        }

        .office-hours-item span:last-child {
          font-weight: 600;
        }

        /* Contact Form Card */
        .contact-form-card {
          background: rgba(255, 255, 255, 0.5);
          backdrop-filter: blur(20px) saturate(140%);
          border-radius: 24px;
          padding: 32px;
          border: 1px solid var(--clr-blue-border);
        }

        .contact-form-card h3 {
          font-family: 'Playfair Display', serif;
          margin-bottom: 25px;
        }

        .form-group {
          margin-bottom: 20px;
        }

        .form-group input,
        .form-group textarea {
          width: 100%;
          padding: 14px 18px;
          border: 1px solid var(--clr-blue-border);
          border-radius: 12px;
          background: rgba(255, 255, 255, 0.6);
          font-size: 0.9rem;
          transition: all 0.3s ease;
          font-family: inherit;
          box-sizing: border-box;
        }

        .form-group input:focus,
        .form-group textarea:focus {
          outline: none;
          border-color: var(--clr-blue);
          background: rgba(255, 255, 255, 0.8);
        }

        .form-group textarea {
          resize: vertical;
          min-height: 120px;
        }

        .submit-btn {
          width: 100%;
          padding: 14px;
          background: linear-gradient(135deg, var(--clr-blue), #0284c7);
          border: none;
          border-radius: 40px;
          color: white;
          font-weight: 700;
          font-size: 1rem;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .submit-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(14, 165, 233, 0.3);
        }

        /* Map */
        .contact-map {
          border-radius: 24px;
          overflow: hidden;
          height: 300px;
          margin-bottom: 60px;
        }

        .contact-map iframe {
          width: 100%;
          height: 100%;
          border: none;
        }

        /* Mobile Responsive */
        @media (max-width: 1024px) {
          .contact-grid {
            gap: 24px;
          }

          .contact-info-card,
          .contact-form-card {
            padding: 28px;
          }
        }

        @media (max-width: 768px) {
          .contact-page {
            padding-top: 20px;
          }

          .contact-page-title {
            font-size: 1.8rem;
            margin-bottom: 20px;
          }

          .contact-grid {
            grid-template-columns: 1fr;
            gap: 24px;
            margin-bottom: 40px;
          }

          .contact-info-card,
          .contact-form-card {
            padding: 24px;
          }

          .contact-info-item {
            padding: 10px;
            gap: 14px;
            margin-bottom: 15px;
          }

          .contact-icon {
            width: 48px;
            height: 48px;
            font-size: 1.1rem;
          }

          .contact-info-item h4 {
            font-size: 0.95rem;
          }

          .contact-info-item p {
            font-size: 0.8rem;
          }

          .office-hours-item {
            padding: 8px 0;
            font-size: 0.9rem;
          }

          .form-group input,
          .form-group textarea {
            padding: 12px 16px;
            font-size: 0.85rem;
          }

          .submit-btn {
            padding: 12px;
            font-size: 0.95rem;
          }

          .contact-map {
            height: 250px;
            margin-bottom: 40px;
          }
        }

        @media (max-width: 480px) {
          .contact-page-title {
            font-size: 1.5rem;
            margin-bottom: 15px;
          }

          .contact-grid {
            gap: 16px;
            margin-bottom: 30px;
          }

          .contact-info-card,
          .contact-form-card {
            padding: 18px;
            border-radius: 16px;
          }

          .contact-info-card h3,
          .contact-form-card h3 {
            font-size: 1.2rem;
            margin-bottom: 18px;
          }

          .contact-info-item {
            padding: 8px;
            gap: 12px;
            margin-bottom: 12px;
          }

          .contact-icon {
            width: 40px;
            height: 40px;
            font-size: 0.9rem;
          }

          .contact-info-item h4 {
            font-size: 0.85rem;
          }

          .contact-info-item p {
            font-size: 0.75rem;
          }

          .office-hours h4 {
            font-size: 1rem;
          }

          .office-hours-item {
            padding: 6px 0;
            font-size: 0.8rem;
          }

          .form-group {
            margin-bottom: 14px;
          }

          .form-group input,
          .form-group textarea {
            padding: 10px 14px;
            font-size: 0.8rem;
            border-radius: 10px;
          }

          .form-group textarea {
            min-height: 100px;
          }

          .submit-btn {
            padding: 10px;
            font-size: 0.85rem;
            border-radius: 30px;
          }

          .contact-map {
            height: 200px;
            border-radius: 16px;
            margin-bottom: 30px;
          }
        }

        @media (max-width: 360px) {
          .contact-page-title {
            font-size: 1.3rem;
          }

          .contact-info-card,
          .contact-form-card {
            padding: 14px;
          }

          .contact-icon {
            width: 36px;
            height: 36px;
            font-size: 0.8rem;
          }

          .contact-info-item {
            padding: 6px;
            gap: 10px;
          }

          .contact-info-item h4 {
            font-size: 0.8rem;
          }

          .contact-info-item p {
            font-size: 0.7rem;
          }

          .office-hours-item {
            font-size: 0.75rem;
            padding: 5px 0;
          }

          .form-group input,
          .form-group textarea {
            padding: 8px 12px;
            font-size: 0.75rem;
          }

          .submit-btn {
            padding: 8px;
            font-size: 0.8rem;
          }

          .contact-map {
            height: 180px;
          }
        }
      `}</style>
    </div>
  );
};

export default Contact;